import { useEffect, useMemo, useState } from "react";
import {
  IParcelamento,
  IProduto,
  IProdutoCombinacao,
} from "shared/interfaces/IProduto";

export interface EstadoValor {
  /**
   * A loja vende alguma combinação com este valor e o resto já escolhido.
   * Falso: a combinação não existe (ex.: "Vender" desligado) e o valor some.
   */
  visivel: boolean;
  /** Alguma dessas combinações tem estoque. Falso: esgotado, bloqueado. */
  habilitado: boolean;
}

export interface SelecaoCombinacao {
  /** Valor escolhido por variação: { [variationId]: valueId }. */
  selecao: Record<string, string>;
  selecionar: (variacaoId: string, valorId: string) => void;
  estadoDoValor: (variacaoId: string, valorId: string) => EstadoValor;
  /** Combinação escolhida por completo; null enquanto falta alguma variação. */
  combinacao: IProdutoCombinacao | null;
  /** Preço a mostrar: o da combinação, "a partir de" antes disso, ou o do produto. */
  preco: number;
  aPartirDe: boolean;
  parcelamento: IParcelamento | null;
  /** Estoque que limita a quantidade; null quando ainda não há como saber. */
  estoque: number | null;
  /** Pode comprar: sem variações, ou com combinação escolhida e disponível. */
  podeComprar: boolean;
}

function combina(
  combinacao: IProdutoCombinacao,
  selecao: Record<string, string>,
  ignorar?: string,
): boolean {
  return Object.entries(selecao).every(
    ([variacaoId, valorId]) =>
      variacaoId === ignorar || combinacao.values[variacaoId] === valorId,
  );
}

/**
 * Seleção da combinação na página do produto. Valores que não formam uma
 * combinação vendida com o que já foi escolhido ficam desabilitados, e uma
 * variação com um único valor já vem escolhida.
 */
export function useSelecaoCombinacao(produto: IProduto): SelecaoCombinacao {
  const variacoes = useMemo(() => produto.variations ?? [], [produto]);
  const combinacoes = useMemo(() => produto.combinations ?? [], [produto]);

  const [selecao, setSelecao] = useState<Record<string, string>>({});

  useEffect(() => {
    setSelecao(
      Object.fromEntries(
        variacoes
          // Só pré-escolhe um valor único que tenha estoque: esgotado,
          // ele fica bloqueado.
          .filter(
            (variacao) =>
              variacao.values.length === 1 &&
              combinacoes.some(
                (combinacao) =>
                  combinacao.available &&
                  combinacao.values[variacao.id] === variacao.values[0].id,
              ),
          )
          .map((variacao) => [variacao.id, variacao.values[0].id]),
      ),
    );
  }, [variacoes, combinacoes]);

  function selecionar(variacaoId: string, valorId: string) {
    setSelecao((atual) => {
      const proxima = { ...atual, [variacaoId]: valorId };
      // Escolhas que deixaram de formar uma combinação com o novo valor saem.
      for (const outraId of Object.keys(proxima)) {
        if (outraId === variacaoId) continue;
        const aindaCombina = combinacoes.some(
          (combinacao) =>
            combinacao.available &&
            combinacao.values[variacaoId] === valorId &&
            combinacao.values[outraId] === proxima[outraId],
        );
        if (!aindaCombina) delete proxima[outraId];
      }
      return proxima;
    });
  }

  function estadoDoValor(variacaoId: string, valorId: string): EstadoValor {
    const possiveis = combinacoes.filter(
      (combinacao) =>
        combinacao.values[variacaoId] === valorId &&
        combina(combinacao, selecao, variacaoId),
    );
    return {
      visivel: possiveis.length > 0,
      habilitado: possiveis.some((combinacao) => combinacao.available),
    };
  }

  const completa =
    variacoes.length > 0 &&
    variacoes.every((variacao) => selecao[variacao.id] !== undefined);
  const combinacao = completa
    ? (combinacoes.find((item) => combina(item, selecao)) ?? null)
    : null;

  if (!produto.hasVariations) {
    const estoque = produto.estoque ?? 0;
    return {
      selecao,
      selecionar,
      estadoDoValor,
      combinacao: null,
      preco: Number(produto.price) || 0,
      aPartirDe: false,
      parcelamento: produto.installment ?? null,
      estoque,
      podeComprar: estoque > 0,
    };
  }

  return {
    selecao,
    selecionar,
    estadoDoValor,
    combinacao,
    preco: combinacao?.price ?? produto.priceMin ?? 0,
    aPartirDe: !combinacao,
    parcelamento: combinacao?.installment ?? null,
    estoque: combinacao?.stock ?? null,
    podeComprar: Boolean(combinacao?.available),
  };
}
